import React from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import Input from "../../../component-lib/Atoms/Input/Input";
import Button from "../../../component-lib/Molecules/button/Button";
import ContactInput from "../../../component-lib/Atoms/Input/ContactInput";
import { ADD_NEW_DEPARTMENT_DATA } from "../constants";
import EventEHRUploadImageCard from "../../../component-lib/Molecules/upload-image-card/EventEHRUploadImageCard";
import BootstrapDialog from "../../../utils/bootstrap-dialog-style/BootstrapDialog";

interface AddProviderUserDialogProps {
  isOpen: boolean;
  handleData: (data: any) => void;
  handleClose: () => void;
}

export default function AddProviderUserDialog({
  isOpen = false,
  handleClose,
  handleData,
}: AddProviderUserDialogProps) {
  const validationSchema = Yup.object().shape({
    [ADD_NEW_DEPARTMENT_DATA?.DEPARTMENT_ID]: Yup.number().required(
      "Department id is required"
    ),
    [ADD_NEW_DEPARTMENT_DATA?.DEPARTMENT_NAME]: Yup.string().required(
      "Department name is required"
    ),
    [ADD_NEW_DEPARTMENT_DATA?.DEPARTMENT_ADMIN]: Yup.string().required(
      "Department admin is required"
    ),
    [ADD_NEW_DEPARTMENT_DATA?.DEPARTMENT_CONTACT_NO]: Yup.number().required(
      "Contact number is required"
    ),
  });

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
    >
      <Formik
        initialValues={{
          [ADD_NEW_DEPARTMENT_DATA?.DEPARTMENT_ID]: "",
          [ADD_NEW_DEPARTMENT_DATA?.DEPARTMENT_NAME]: "",
          [ADD_NEW_DEPARTMENT_DATA?.DEPARTMENT_ADMIN]: "",
          [ADD_NEW_DEPARTMENT_DATA?.DEPARTMENT_CONTACT_NO]: "",
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          try {
            // getFormData(values);
            handleData(values);
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false); // Reset submitting state
          }
        }}
      >
        {({
          errors,
          values,
          touched,
          isValid,
          dirty,
          setFieldValue,
          handleBlur,
          handleSubmit,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="customized-dialog-title"
              >
                Add Provider User
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              {/* MAIN CONTENT */}
              <DialogContent>
                <div className="pt-4">
                  <EventEHRUploadImageCard
                    imageUrl={null}
                    handleImage={(data) => {}}
                  />
                </div>

                <div
                  style={{
                    // height: deviceType === "mobile" ? "40vh" : "fit-content",
                    overflow: "auto",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  {/* DEPARTMENT ID TEXTFIELD */}
                  <div className="pt-4">
                    <Input
                      textFieldLabel="Enter Department ID"
                      // placeholder="Enter Department ID"
                      size="small"
                      type="number"
                      name={ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_ID}
                      value={values[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_ID]}
                      onChangeCb={(e) => {
                        setFieldValue(
                          ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_ID,
                          e.target.value
                        );
                      }}
                      onBlur={handleBlur}
                      error={
                        touched[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_ID] &&
                        Boolean(errors[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_ID])
                      }
                      helperText={
                        touched[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_ID]
                          ? errors[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_ID]
                          : undefined
                      }
                    />
                  </div>

                  {/* DEPARTMENT NAME TEXTFIELD */}
                  <div className="pt-4">
                    <Input
                      textFieldLabel="Enter Department Name"
                      // placeholder="Enter Department ID"
                      size="small"
                      name={ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_NAME}
                      value={values[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_NAME]}
                      onChangeCb={(e) => {
                        setFieldValue(
                          ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_NAME,
                          e.target.value
                        );
                      }}
                      onBlur={handleBlur}
                      error={
                        touched[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_NAME] &&
                        Boolean(errors[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_NAME])
                      }
                      helperText={
                        touched[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_NAME]
                          ? errors[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_NAME]
                          : undefined
                      }
                    />
                  </div>

                  {/* DEPARTMENT ADMIN TEXTFIELD */}
                  <Input
                    textFieldLabel="Enter Department Admin"
                    // placeholder="Enter Department ID"
                    size="small"
                    name={ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_ADMIN}
                    value={values[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_ADMIN]}
                    onChangeCb={(e) => {
                      setFieldValue(
                        ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_ADMIN,
                        e.target.value
                      );
                    }}
                    onBlur={handleBlur}
                    error={
                      touched[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_ADMIN] &&
                      Boolean(errors[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_ADMIN])
                    }
                    helperText={
                      touched[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_ADMIN]
                        ? errors[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_ADMIN]
                        : undefined
                    }
                  />
                  {/* CONTACT INPUT TEXTFIELD */}
                  <ContactInput
                    textFieldLabel="Contact Number"
                    name={ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_CONTACT_NO}
                    value={
                      values[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_CONTACT_NO]
                    }
                    onChangeCb={(value) => {
                      setFieldValue(
                        ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_CONTACT_NO,
                        value
                      );
                    }}
                    onBlur={handleBlur}
                    size="small"
                    error={
                      touched[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_CONTACT_NO] &&
                      Boolean(
                        errors[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_CONTACT_NO]
                      )
                    }
                    helperText={
                      touched[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_CONTACT_NO]
                        ? errors[ADD_NEW_DEPARTMENT_DATA.DEPARTMENT_CONTACT_NO]
                        : undefined
                    }
                    isContactNumber={true}
                  />
                </div>
              </DialogContent>

              {/* ACTION BUTTON */}
              <DialogActions style={{ padding: "24px" }}>
                <Button
                  type="submit"
                  label={TEXT_CONSTANTS?.SAVE}
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    padding: "8px 55px",
                    fontSize: "16px",
                  }}
                />
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
